<script>
    import { mapActions, mapState } from "vuex";
    import { lazyLoad } from "@/utils";
    import { metaInfo } from "@/core/MetaInfo";
    import QueryBuilder from "@/api/query/query-builder";
    import NotEquals from "@/api/query/filters/not-equals";
    import In from "@/api/query/filters/in";
    import Newsletter from "@miku-kz/components/Newsletter";
    import GuaranteeList from "@miku-kz/components/GuaranteeList";
    import RecipeProducts from "@miku-kz/components/recipes/RecipeProducts";
    import RecipeOthers from "@miku-kz/components/recipes/RecipeOthers";
    import RecipeDetailText from "@miku-kz/components/recipes/RecipeDetailText";
    import RecipeDetailCard from "@miku-kz/components/recipes/RecipeDetailCard";
    import RecipeDetailLoading from "@miku-kz/components/recipes/RecipeDetailLoading";

    export default {
        name: "RecipeDetail",
        components: {
            RecipeDetailLoading,
            GuaranteeList,
            RecipeDetailCard,
            RecipeDetailText,
            RecipeOthers,
            RecipeProducts,
            Newsletter,
        },
        computed: {
            ...mapState("Recipes", [
                "recipeDetail",
                "recipeOthers",
                "recipeProducts",
                "globalLoading",
            ]),
            slug() {
                return this.$route.params.slug;
            },
            meta() {
                return {
                    title: this.recipeDetail?.name || "",
                };
            },
        },
        watch: {
            async $route(value) {
                let { id, products } = await this.getRecipeDetail(value.params.slug);
                if (products.length > 0) {
                    await this.getRecipeProducts(products);
                }
                await this.getRecipeOthersWithQuery(id);
            },
            recipeProducts: {
                handler() {
                    this.$nextTick(() => {
                        lazyLoad();
                    });
                },
                immediate: true,
            },
        },
        async serverPrefetch() {
            let { id, products } = await this.getRecipeDetail(this.slug);
            if (products.length > 0) {
                await this.getRecipeProducts(products);
            }

            return this.getRecipeOthersWithQuery(id);
        },
        destroyed() {
            this.clearRecipe();
        },
        async mounted() {
            if (!this.recipeDetail.name) {
                let { id, products } = await this.getRecipeDetail(this.slug);
                if (products.length > 0) {
                    await this.getRecipeProducts(products);
                }
                await this.getRecipeOthersWithQuery(id);
            }
        },
        methods: {
            ...mapActions("Recipes", [
                "getRecipeDetail",
                "getRecipeOthers",
                "getRecipeProducts",
                "clearRecipe",
            ]),
            lazyLoad,
            async getRecipeOthersWithQuery(id) {
                const buildQuery = [
                    new In("tags", this.recipeDetail.tags),
                    new NotEquals("id", id),
                ];

                const queryBuilder = new QueryBuilder(buildQuery);
                const query = "?" + queryBuilder.build();

                return await this.getRecipeOthers(query);
            },
        },
        metaInfo() {
            return metaInfo.getProductMeta({
                title: this.meta.title,
                slug: `recipes/${this.slug}`,
            });
        },
    };
</script>

<template>
    <div class="l-container">
        <div class="recipe-detail">
            <recipe-detail-card
                v-if="!globalLoading && recipeDetail"
                :recipe="recipeDetail"
            ></recipe-detail-card>
            <recipe-detail-loading v-else></recipe-detail-loading>

            <div class="recipe-detail__inner">
                <div class="recipe-detail__text">
                    <recipe-detail-text
                        v-if="recipeDetail.howToCook"
                        title="Как готовить"
                        :text="recipeDetail.howToCook"
                    ></recipe-detail-text>

                    <recipe-detail-text
                        v-if="recipeDetail.recommendation"
                        title="Рекомендации"
                        :text="recipeDetail.recommendation"
                    ></recipe-detail-text>
                </div>

                <div v-if="recipeProducts.length > 0" class="recipe-detail__products">
                    <recipe-products :products="recipeProducts"></recipe-products>
                </div>
            </div>

            <div v-if="recipeOthers.length > 0" class="recipe-detail__others">
                <recipe-others :recipes="recipeOthers"></recipe-others>
            </div>

            <div class="recipe-detail__subscribe">
                <newsletter></newsletter>
            </div>

            <div class="recipe-detail__guarantee">
                <guarantee-list></guarantee-list>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .recipe-detail {
        padding: 40px 0;
        @include bp($bp-desktop-sm) {
            padding-bottom: 112px;
        }
    }

    .recipe-detail__section {
        margin-bottom: 16px;
        border-radius: 15px;
        box-shadow: 0 0 15px #f2f2f5;
        @include bp($bp-desktop-sm) {
            margin-bottom: 40px;
            border-radius: 20px;
            box-shadow: 0 10px 30px #f2f2f5;
        }
    }

    .recipe-detail__inner {
        @include bp($bp-desktop-sm) {
            display: flex;
        }
    }

    .recipe-detail__text {
        @include bp($bp-desktop-sm) {
            flex-basis: 990px;
            flex-grow: 1;
            flex-shrink: 0;
        }
    }

    .recipe-detail__products {
        margin-bottom: 40px;
        @include bp($bp-desktop-sm) {
            max-width: 230px;
            margin-left: 40px;
        }
    }

    .recipe-detail__loading {
        min-height: 800px;
    }

    .recipe-detail__container {
        display: flex;
        flex-direction: column;
    }

    .recipe-detail__others,
    .recipe-detail__subscribe {
        margin-bottom: 40px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 56px;
        }
    }
</style>
